import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogCard from './components/BlogCard';
import BlogSidebar from './components/BlogSidebar';
import { blogPosts, blogCategories } from '@/mocks/blogPosts';

export default function BlogListing() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language.startsWith('ar');
  const [activeCategory, setActiveCategory] = useState('all');

  const displayedPosts = activeCategory === 'all'
    ? blogPosts
    : blogPosts.filter((p) => p.category.toLowerCase().replace(/[\s&]+/g, '-') === activeCategory);

  const blogLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': 'https://debugin.com/blog/#blog',
    'name': 'Texio Blog',
    'description': 'Insights, guides, and best practices for textile manufacturers navigating digital transformation. Expert articles on ERP selection, quality control, AI in manufacturing, and supply chain optimization.',
    'url': 'https://debugin.com/blog',
    'publisher': {
      '@id': 'https://debugin.com/#organization',
    },
    'inLanguage': ['en', 'ar'],
    'blogPost': blogPosts.map((p) => ({
      '@type': 'BlogPosting',
      'headline': p.title,
      'description': p.excerpt,
      'url': `https://debugin.com/blog/${p.slug}`,
      'datePublished': p.date,
      'author': {
        '@type': 'Person',
        'name': p.author,
      },
      'image': p.imageUrl,
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <title>Texio Blog - Textile Manufacturing Insights | ERP Guides & Industry Trends</title>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogLd) }}
      />

      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-24 md:pt-32 pb-10 md:pb-14">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground-950 mb-4">
                {t('blog.title')}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {t('blog.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter Pills */}
        <section className="pb-8">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-2">
              {blogCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-white text-foreground-600 border border-background-200/60 hover:border-primary-200/60 hover:text-primary-600'
                  }`}
                >
                  {isAr ? cat.nameAr : cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid + Sidebar */}
        <section className="pb-16 md:pb-24">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                {/* Main Content */}
                <div className="flex-1">
                  {displayedPosts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                      {displayedPosts.map((post) => (
                        <BlogCard key={post.slug} post={post} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <p className="text-foreground-500">No posts found in this category.</p>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="w-full lg:w-72 shrink-0">
                  <BlogSidebar />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}